import { getPaymentAccountingDate, getExpenseAccountingDate } from '../../accounting/utils/accountingDates';
import { getDealObligations, isCancelledRecord, normalizedStatus } from './dashboard.obligations';

const cents = (amount) => Number.isFinite(Number(amount)) ? Math.round(Number(amount) * 100) : 0;
const dateKey = (value) => value instanceof Date
  ? `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-${String(value.getDate()).padStart(2, '0')}`
  : String(value || '').slice(0, 10);
const interval = { mensile: 1, trimestrale: 3, annuale: 12 };

export function getAnnualFinancialOutlook({ payments = [], expenses = [], ...deals } = {}, range = {}, today = new Date()) {
  const reference = range?.to || range?.from || today;
  const parsedYear = Number(dateKey(reference).slice(0, 4));
  const year = parsedYear >= 1900 && parsedYear <= 9999 ? parsedYear : today.getFullYear();
  const from = `${year}-01-01`, to = `${year}-12-31`, todayKey = dateKey(today);
  const inYear = (date) => dateKey(date) >= from && dateKey(date) <= to;
  const activePayments = payments.filter((row) => !isCancelledRecord(row));
  const activeExpenses = expenses.filter((row) => !isCancelledRecord(row));
  const paid = (row) => normalizedStatus(row.status) === 'pagato';
  const received = (row) => normalizedStatus(row.status) === 'incassato';
  const receipts = activePayments.filter((row) => received(row) && inYear(row.paymentDate || getPaymentAccountingDate(row)));
  const receivables = activePayments.filter((row) => !received(row) && inYear(row.dueDate));
  const settledCosts = activeExpenses.filter((row) => paid(row) && inYear(row.paymentDate || getExpenseAccountingDate(row)));
  const payables = activeExpenses.filter((row) => !paid(row) && inYear(row.dueDate));
  const obligations = getDealObligations({ payments, ...deals }, { includeProposals: false }).filter((row) => inYear(row.dueDate));
  const knownDealAmounts = obligations.filter((row) => row.amount != null && Number.isFinite(row.amount));
  let recurrenceCents = 0, recurrenceCount = 0;
  // Extend only explicitly configured series, preserving their end and month anchor.
  // Existing occurrences (including cancelled or rescheduled ones) always win.
  activeExpenses.filter((row) => row.id != null && row.recurrenceParentId == null && interval[row.recurring]).forEach((origin) => {
    const anchor = new Date(`${dateKey(origin.dueDate)}T00:00:00`), step = interval[origin.recurring];
    if (Number.isNaN(anchor.getTime())) return;
    const firstIndex = Math.max(1, Math.floor(((year - anchor.getFullYear()) * 12 - anchor.getMonth()) / step));
    for (let index = firstIndex; ; index++) {
      if (origin.recurrenceCount != null && index >= Number(origin.recurrenceCount)) break;
      const date = new Date(anchor.getFullYear(), anchor.getMonth() + index * step, 1);
      date.setDate(Math.min(anchor.getDate(), new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()));
      const key = dateKey(date);
      if (key > to) break;
      if (key < from || key <= todayKey) continue;
      const exists = expenses.some((row) => String(row.recurrenceParentId) === String(origin.id)
        && (Number(row.recurrenceNumber) === index + 1 || dateKey(row.recurrenceGeneratedForDate || row.dueDate) === key));
      if (!exists) { recurrenceCents += cents(origin.amount); recurrenceCount++; }
    }
  });
  const sum = (rows) => rows.reduce((total, row) => total + cents(row.amount), 0);
  const receivedCents = sum(receipts), paidCents = sum(settledCosts);
  const pendingIncomeCents = sum(receivables) + sum(knownDealAmounts);
  const pendingCostCents = sum(payables) + recurrenceCents;
  return {
    year, from, to, ricavi: (receivedCents + pendingIncomeCents) / 100, costi: (paidCents + pendingCostCents) / 100,
    utile: (receivedCents + pendingIncomeCents - paidCents - pendingCostCents) / 100,
    incassati: receivedCents / 100, pagati: paidCents / 100, daIncassare: pendingIncomeCents / 100, daPagare: pendingCostCents / 100,
    saldoRealizzato: (receivedCents - paidCents) / 100, ricorrenzePianificate: recurrenceCount,
    importoRicorrenzePianificate: recurrenceCents / 100, scadenzeSenzaImporto: obligations.length - knownDealAmounts.length,
    hasData: receipts.length + receivables.length + settledCosts.length + payables.length + obligations.length + recurrenceCount > 0,
    unavailableModules: (deals.overdueUnavailableModules || []).filter((module) => ['Affari', 'Incassi e uscite'].includes(module)),
    crescita: null, crescitaDisponibile: false,
  };
}
